import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import NotificationService from "./NotificationService";

const STORAGE_KEY = "mygoal-scheduled-activity-reminders";
const CHANNEL_ID = "mygoal-athlete-reminders";
const WEB_TIMEOUTS = new Map();
const NATIVE_PERMISSION_KEY = "mygoal-native-notification-permission";

const getStoredReminders = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error("Failed to read scheduled reminders:", error);
    return {};
  }
};

const saveStoredReminders = (value) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
};

const isAndroidNative = () => {
  try {
    return Capacitor.getPlatform() === "android";
  } catch (error) {
    return false;
  }
};

const buildHash = (input) => {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
};

const buildReminderKey = (userId, activityId, reminderType) => `${userId}:${activityId}:${reminderType}`;

const buildReminderId = (key) => buildHash(key);

const clearWebTimeout = (key) => {
  const timeoutId = WEB_TIMEOUTS.get(key);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
    WEB_TIMEOUTS.delete(key);
  }
};

const formatTimeLabel = (date) => date.toLocaleTimeString([], {
  hour: "2-digit",
  minute: "2-digit",
});

const buildReminderBody = (activity, reminderType) => {
  if (reminderType === "morning") {
    return `You have training today at ${formatTimeLabel(activity.startAt)}. Choose your goal before you start.`;
  }

  return 'Your activity has finished. Take a moment to fill in your rating.';
};

const buildReminderTitle = (activity, reminderType) => {
  if (reminderType === "morning") {
    return 'Set your activity goal';
  }

  return 'Rate your activity';
};

export const buildReminderEntries = (userId, activities, preferences = {}, ratedActivityIds = [], goalActivityIds = []) => {
  const now = Date.now();

  return activities.flatMap((activity) => {
    if (!activity?.id || !(activity.startAt instanceof Date) || Number.isNaN(activity.startAt.getTime())) {
      return [];
    }

    const endAt = activity.endAt instanceof Date && !Number.isNaN(activity.endAt.getTime())
      ? activity.endAt
      : new Date(activity.startAt.getTime() + ((activity.duration || 60) * 60 * 1000));

    const morningAt = new Date(activity.startAt);
    morningAt.setHours(9, 0, 0, 0);

    const entries = [];

    if (preferences.goal_reminders !== false && ['practice', 'training', 'team_training', 'specialist_training', 'strength_training', 'conditioning_training', 'cardio', 'strength'].includes(activity.type || 'practice') && morningAt.getTime() > now && morningAt < activity.startAt && !goalActivityIds.some(id => id === activity.id || id === `team_${activity.id}`)) {
      entries.push({
        key: buildReminderKey(userId, activity.id, "morning"),
        userId,
        activityId: activity.id,
        reminderType: "morning",
        at: morningAt.toISOString(),
        title: buildReminderTitle(activity, "morning"),
        body: buildReminderBody(activity, "morning"),
      });
    }

    const ratingAt = new Date(endAt.getTime() + (30 * 60 * 1000));
    if (preferences.rating_reminders !== false && ratingAt.getTime() > now && !ratedActivityIds.includes(activity.id)) {
      entries.push({
        key: buildReminderKey(userId, activity.id, "rating"),
        userId,
        activityId: activity.id,
        reminderType: "rating",
        at: ratingAt.toISOString(),
        title: buildReminderTitle(activity, "rating"),
        body: buildReminderBody(activity, "rating"),
      });
    }

    return entries;
  });
};

const ensureNativePermissions = async () => {
  const current = await LocalNotifications.checkPermissions();
  if (current.display === "granted") {
    localStorage.setItem(NATIVE_PERMISSION_KEY, "granted");
    return true;
  }

  const requested = await LocalNotifications.requestPermissions();
  localStorage.setItem(NATIVE_PERMISSION_KEY, requested.display);
  if (requested.display !== "granted") {
    return false;
  }

  try {
    await LocalNotifications.createChannel({
      id: CHANNEL_ID,
      name: "Athlete reminders",
      description: "Training reminders and rating nudges",
      importance: 4,
      visibility: 1,
      vibration: true,
    });
  } catch (error) {
    console.log("Notification channel already exists or could not be created:", error);
  }

  return true;
};

const scheduleNativeReminder = async (entry) => {
  const id = buildReminderId(entry.key);
  await LocalNotifications.schedule({
    notifications: [{
      id,
      title: entry.title,
      body: entry.body,
      schedule: { at: new Date(entry.at) },
      channelId: CHANNEL_ID,
      extra: {
        activityId: entry.activityId,
        reminderType: entry.reminderType,
      },
    }],
  });

  return id;
};

const scheduleWebReminder = (entry) => {
  const delay = new Date(entry.at).getTime() - Date.now();
  if (delay <= 0) {
    return null;
  }

  clearWebTimeout(entry.key);
  const timeoutId = window.setTimeout(() => {
    NotificationService.showNotification(entry.title, entry.body, {
      tag: entry.key,
      requireInteraction: entry.reminderType === "morning",
    });
  }, delay);

  WEB_TIMEOUTS.set(entry.key, timeoutId);
  return timeoutId;
};

const removeReminderEntry = async (key, meta) => {
  clearWebTimeout(key);

  if (isAndroidNative() && meta?.nativeId) {
    try {
      await LocalNotifications.cancel({ notifications: [{ id: meta.nativeId }] });
    } catch (error) {
      console.error("Failed to cancel native reminder:", error);
    }
  }
};

export const ActivityReminderService = {
  async syncActivityReminders({ userId, activities, preferences, ratedActivityIds, goalActivityIds }) {
    if (!userId) return { scheduled: 0 };

    const usingNative = isAndroidNative();
    const hasPermission = usingNative
      ? await ensureNativePermissions()
      : NotificationService.getPermission() === "granted";

    if (!hasPermission) {
      return { scheduled: 0 };
    }

    const nextEntries = buildReminderEntries(userId, activities, preferences, ratedActivityIds, goalActivityIds);
    const stored = getStoredReminders();
    const updatedStore = { ...stored };
    const desiredKeys = new Set(nextEntries.map((entry) => entry.key));

    for (const [key, meta] of Object.entries(stored)) {
      if (meta.userId === userId && !desiredKeys.has(key)) {
        await removeReminderEntry(key, meta);
        delete updatedStore[key];
      }
    }

    for (const entry of nextEntries) {
      const previous = stored[entry.key];
      const unchanged = previous && previous.at === entry.at && previous.title === entry.title && previous.body === entry.body;

      if (unchanged) {
        if (!usingNative && !WEB_TIMEOUTS.has(entry.key)) {
          scheduleWebReminder(entry);
        }
        continue;
      }

      if (previous) {
        await removeReminderEntry(entry.key, previous);
      }

      const nativeId = usingNative ? await scheduleNativeReminder(entry) : null;
      if (!usingNative) {
        scheduleWebReminder(entry);
      }

      updatedStore[entry.key] = {
        userId,
        activityId: entry.activityId,
        reminderType: entry.reminderType,
        at: entry.at,
        title: entry.title,
        body: entry.body,
        nativeId,
      };
    }

    saveStoredReminders(updatedStore);
    return { scheduled: nextEntries.length };
  },

  async cancelActivityRatingReminder(userId, activityId) {
    if (!userId || !activityId) return;

    const key = buildReminderKey(userId, activityId, "rating");
    const stored = getStoredReminders();
    const meta = stored[key];

    if (!meta) return;

    await removeReminderEntry(key, meta);
    delete stored[key];
    saveStoredReminders(stored);
  },
};

export default ActivityReminderService;